import { Dropdown, Flex, Button, Row, Col, Skeleton } from "antd"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Title } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { debounce } from "lodash"
import styles from "./index.module.css"
import SearchInput from "ui/SearchInput"
import { DownOutlined } from "@ant-design/icons"
import { spacings } from "themes/variables"
import SortOverlay from "./SortOverlay"
import SORT from "./constants"
import RatingSummaryCard from "./RatingSummaryCard"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { getGuideReviews } from "store/guide-profile"
import ReviewItemList from "./ReviewItemList"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import EmptyStateUI from "./EmptyStateUI"

const DELAY_TIME = 300

const getSortDropdownContent = ({ setSort, setIsSortingDropdownOpen, sortDraft, setSortDraft }) => (
  <SortOverlay setSort={setSort} setOpen={setIsSortingDropdownOpen} sortDraft={sortDraft} setSortDraft={setSortDraft} />
)

const GuideReviews = () => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState("")
  const [isSortingDropdownOpen, setIsSortingDropdownOpen] = useState(false)
  const [sort, setSort] = useState(SORT.DATE_NEW)
  const [sortDraft, setSortDraft] = useState(sort)
  const dispatch = useDispatch()
  const { slug } = useParams()
  const isFirstSearchEffect = useRef(true)
  const { loading, data } = useSelector((store) => store.guideProfile?.reviews)

  const debouncedFetch = useMemo(
    () =>
      debounce((term, sortVal, slugVal) => {
        const params = {
          page: 1,
          per_page: 10,
          review_mode: true,
          ...(sortVal && { sort_by: sortVal }),
          search: term
        }
        dispatch(getGuideReviews(slugVal, params))
      }, DELAY_TIME),
    [dispatch]
  )

  useEffect(() => {
    debouncedFetch.cancel()
    const params = {
      page: 1,
      per_page: 10,
      review_mode: true,
      ...(sort && { sort_by: sort })
    }
    dispatch(getGuideReviews(slug, params))
  }, [slug, sort])

  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false
      return
    }
    debouncedFetch(searchTerm, sort, slug)
    return () => debouncedFetch.cancel()
  }, [searchTerm, debouncedFetch])

  const getRatingLabel = (value) => {
    if (value === SORT.RATING_HIGH) return t("reviews.rating_high_to_low", "High to Low")
    if (value === SORT.RATING_LOW) return t("reviews.rating_low_to_high", "Low to High")
    return ""
  }
  const getDateLabel = (value) => {
    if (value === SORT.DATE_NEW) return t("reviews.date_new_to_old", "New to Old")
    if (value === SORT.DATE_OLD) return t("reviews.date_old_to_new", "Old to New")
    return ""
  }
  const triggerText = `${t("reviews.sort_by", "Sort by")} ${
    sort === SORT.RATING_HIGH || sort === SORT.RATING_LOW
      ? `${t("reviews.rating", "Rating")}: ${getRatingLabel(sort)}`
      : `${t("reviews.date", "Date")}: ${getDateLabel(sort)}`
  }`

  return (
    <Flex vertical gap={spacings.XL}>
      <Flex wrap justify="space-between" className="w-full" align="center">
        <Skeleton loading={loading} active paragraph={{ rows: 1 }}>
          <Title level={3}>
            {data?.total || 0} {t("reviews.title", "Review(s)")}
          </Title>
        </Skeleton>
        <Flex wrap align="center" gap={spacings.XS}>
          <SearchInput searchBy={searchTerm} setSearchBy={setSearchTerm} placeholder={t("global.search")} className={styles.searchInput} />
          <Dropdown
            open={isSortingDropdownOpen}
            onOpenChange={(next) => {
              setIsSortingDropdownOpen(next)
            }}
            trigger={["click"]}
            placement="bottomRight"
            dropdownRender={() => getSortDropdownContent({ setSort, setIsSortingDropdownOpen, sortDraft, setSortDraft })}
          >
            <Button className={styles.sortTrigger}>
              {triggerText}
              <DownOutlined />
            </Button>
          </Dropdown>
        </Flex>
      </Flex>
      <FirstLoading loading={loading} name="guideProfile.reviews">
        <WithEmptyStateWrapper name="reviews" hasItems={data?.total > 0} fallback={<EmptyStateUI isSearch={Boolean(searchTerm)} />}>
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={8}>
              <RatingSummaryCard />
            </Col>
            <Col xs={24} lg={16}>
              <ReviewItemList sort={sort} searchTerm={searchTerm} />
            </Col>
          </Row>
        </WithEmptyStateWrapper>
      </FirstLoading>
    </Flex>
  )
}

export default GuideReviews
